import { Box, Grid, Toolbar } from "@mui/material";
import { StyledComponentProps } from "@mui/styles";
import * as api from "api/Knowledge";
import { FeedClassKey, TimelineClassKey } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterResult } from "components/AdvFilter";
import { CmdWithFile, CmdWithImage, CmdWithVideo, CommandLabels } from "components/AdvFilter/CommonCommands";
import { AwardSnackbarMessage } from "components/AwardSnackbarMessage";
import CardButtonFilter from "components/Buttons/CardButtonFilter";
import IconButtonCancel from "components/Buttons/IconButtonCancel";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import IconCheckComplete from "components/Icons/IconCheckComplete";
import { MuiFilterAppBar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { TextPluralization } from "components/TextPluralization";
import LibUtils from "lib/LibUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { CourseItemCompleteResult, FeedAttachType, FeedContentUpdate, FeedCourseEnrollFilter, FeedItemInteractionCourseEnrollDto } from "models/ApiModels";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import { CourseActions } from "views/Publication/Components/CourseModels";
import CardManager from "views/Timeline/Components/CardManager";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    advFilters: AdvFilterResult[];
    inAdvFilter: boolean;
    dialog: any;
}

interface IProps {
    courseId: string;
    onUpdateEnrollInteraction: (newInteraction: FeedItemInteractionCourseEnrollDto) => void;
}

interface IDispatchProps {
    onRemoveFromFeed: (uid: string, publicationId: string) => void;
    onReloadPublication: (uid: string, publicationId: string) => Promise<void>;
    onExpandPublication: (uid: string, publicationId: string) => Promise<void>;
    onFeedReset: () => void;
    onUpdateFeedData: (newItemDto: FeedContentUpdate | FeedContentUpdate[]) => void;
    onCustomAction: (uid: string, publicationId: string, promiseAction: Promise<CourseItemCompleteResult>) => Promise<CourseItemCompleteResult>;
    onGetFeed: (filter: FeedCourseEnrollFilter, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IStateProps & IDispatchProps & StyledComponentProps<TimelineClassKey | FeedClassKey> & WithSnackbar & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export class FeedCourseEnrollComponent extends React.Component<Props, IState> {

    private filter: FeedCourseEnrollFilter;
    private nextWriteProgress: number = null;
    private currentVideoTime: number = 0;
    private maxProgressTime: number = 0;

    constructor(props: Props) {
        super(props);
        this.state = {
            advFilters: [],
            inAdvFilter: false,
            dialog: null
        };
        this.resetFilter(props);
    }

    ///
    private getItem(uid: string, publicationId: string) {
        const d = this.props.feed.data.find(p => p.uid == uid);
        if (d) {
            const i = d.items.find(p => p.publicationId == publicationId);
            return i;
        }
        return null;
    }

    ///
    private updateItem(uid: string, publicationId: string, result: CourseItemCompleteResult) {
        // Não atualiza informações se não houve atualização dos dados do curso.
        if (result == null) {
            return;
        }

        if (result.award) {
            this.props.snackbar.showMessage(<AwardSnackbarMessage award={result.award} />, "trophy");
        }

        // Avisa o 'chamador' que os dados de estatística do course foram modificados.
        this.props.onUpdateEnrollInteraction(result.courseEnroll);

        // Atualiza o feed com a nova estatística.
        const currentFeed = this.props.feed;
        const tempData = currentFeed.data.find(p => p.uid == uid);
        if (tempData) {
            const upItems: FeedContentUpdate[] = [];
            const item = tempData.items.find(p => p.publicationId == publicationId);
            if (item) {
                // Atualiza o item do curso.
                item.interaction.courseItem = result.courseItem;

                // Atualiza os items do feed com a nova estatística de curso.
                for (var i = 0; i < currentFeed.data.length; i++) {
                    currentFeed.data[i].items[0].interaction.courseEnroll = result.courseEnroll;
                    upItems.push(currentFeed.data[i]);
                }

                this.props.onUpdateFeedData(upItems);
            }
        }
    }

    ///
    private handleCustomAction(uid: string, publicationId: string, publicationType: PublicationType, action: CourseActions): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            if (action.execution) {
                const { actionName } = action.execution;

                switch (actionName) {
                    case "complete":
                        this.props.onCustomAction(uid, publicationId, api.courseEnrollItemComplete(this.props.courseId, publicationId))
                            .then((result) => {
                                this.updateItem(uid, publicationId, result);
                                resolve(false); // retornando FALSE não executa o procedimento padrão (recarregar).
                            })
                            .catch((error) => reject(error));
                        break;

                    default:
                        resolve(false);
                }

            }
            else if (action.player) {
                const { actionName, player, } = action.player;
                const i = this.getItem(uid, publicationId);

                switch (actionName) {

                    case "ready":
                        if (i) {
                            if (i.interaction.courseItem.blockVideoSeek == true) {
                                if (i.interaction.courseItem.initialPlaytime != null) {
                                    player.seek(i.interaction.courseItem.initialPlaytime);
                                    this.maxProgressTime = this.currentVideoTime = i.interaction.courseItem.initialPlaytime;
                                }
                            }
                            this.nextWriteProgress = null;
                        }
                        resolve(false);
                        break;

                    case "seeking": {
                        if (i && i.interaction.courseItem.blockVideoSeek == true) {
                            const seekingTime = player.time;
                            if (seekingTime > Math.max(i.interaction.courseItem.initialPlaytime || 0, this.maxProgressTime)) {
                                player.seek(this.maxProgressTime);
                            }
                        }
                        resolve(false);
                        break;
                    }

                    case "progress":
                        {
                            if (!player.seeking && i != null && i.interaction.courseItem.blockVideoSeek == true) {
                                this.currentVideoTime = player.time;
                                if (this.maxProgressTime < this.currentVideoTime) {
                                    this.maxProgressTime = this.currentVideoTime;
                                }

                                if (this.nextWriteProgress == null || this.nextWriteProgress < this.currentVideoTime) {
                                    this.nextWriteProgress = this.currentVideoTime + 10;
                                    api.courseEnrollPlaytime(this.props.courseId, publicationId, this.currentVideoTime);
                                    // Update local information
                                    i.interaction.courseItem.initialPlaytime = this.currentVideoTime;
                                }
                            }
                            resolve(false);
                            break;
                        }

                    case "ended": {
                        if (i.interaction.courseItem.completedOn == null) {
                            this.props.onCustomAction(uid, publicationId, api.courseEnrollItemComplete(this.props.courseId, publicationId))
                                .then((result) => {
                                    this.updateItem(uid, publicationId, result);
                                    resolve(false); // retornando FALSE não executa o procedimento padrão (recarregar).
                                })
                                .catch((error) => reject(error));
                        }
                        else {
                            resolve(false);
                        }
                        break;
                    }
                }
            }
        });
    }

    //#region FILTER

    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo] },
            { keyword: CmdWithImage, displayName: cmdLabels[CmdWithImage] },
            { keyword: CmdWithFile, displayName: cmdLabels[CmdWithFile] },
        ];
    }

    ///
    private resetFilter(props: Props) {
        this.filter = {
            courseId: props.courseId,
            attachTypes: null,
            searchText: null
        };
    }

    ///
    private isFiltered() {
        return !LibUtils.isAllPropertiesWithValue(this.filter, null, ["courseId"]);
    }

    ///
    private resetAdvFilter() {
        this.resetFilter(this.props);
        this.setState({ advFilters: [], inAdvFilter: false }, () => this.props.onFeedReset());
    }

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        if (searchText.length == 0 && advFilters.length == 0) {
            this.resetAdvFilter();
        }
        else {
            this.filter.searchText = searchText;

            this.filter.attachTypes = [];
            if (advFilters.findIndex(x => x.keyword == CmdWithVideo) > -1) this.filter.attachTypes.push(FeedAttachType.Video);
            if (advFilters.findIndex(x => x.keyword == CmdWithImage) > -1) this.filter.attachTypes.push(FeedAttachType.Image);
            if (advFilters.findIndex(x => x.keyword == CmdWithFile) > -1) this.filter.attachTypes.push(FeedAttachType.File);

            this.setState({ advFilters, inAdvFilter: false }, () => this.props.onFeedReset());
        }
    };

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <AdvFilter
                id="advfilter"
                autoFocus
                commands={this.commands}
                selectedCommands={this.state.advFilters}
                inputValue={this.filter.searchText}
                placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                onEscape={() => this.setState({ inAdvFilter: false })}
                onUpdateSelection={this.handleUpdateFilter}
            />
        );
    }

    ///
    private renderBaseFilter(): JSX.Element {
        const { feed } = this.props;
        return (
            <Grid container justifyContent="space-between" alignItems="center">

                <Grid item>
                    <MuiColor variant={this.isFiltered() ? "warning" : "inherit"}>

                        <CardButtonFilter
                            color="inherit"
                            iconSize="large"
                            onClick={() => { this.setState({ inAdvFilter: true }); }}
                        />

                        <TextPluralization
                            count={feed.totalRecords}
                            resource="Common.Feed.Course.Filter.Result"
                        />

                        {
                            this.isFiltered()
                                ? <IconButtonCancel color="inherit" onClick={() => { this.resetAdvFilter(); }} />
                                : null
                        }
                    </MuiColor>
                </Grid>

            </Grid>
        );
    }

    ///
    private renderFilterBar(): JSX.Element {
        const { feed: { data, totalRecords, loading } } = this.props;
        if (loading) return null;

        const completed = totalRecords == 0 ? 0 : data.filter(p => p.items.filter(x => x.interaction.courseItem.completedOn != null).length > 0).length;
        const progress = data.length == 0 ? 0 : data[0].items[0].interaction.courseEnroll.progress;
        return (
            <MuiFilterAppBar position="static">
                <Toolbar variant="dense" >
                    <Box fontSize="1.1rem" width={1}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            {
                                this.state.inAdvFilter
                                    ? (
                                        <Grid item xs>
                                            {this.state.inAdvFilter && this.renderAdvFilter()}
                                        </Grid>
                                    )
                                    : (
                                        <>
                                            <Grid item>
                                                {this.state.inAdvFilter && this.renderAdvFilter()}
                                                {!this.state.inAdvFilter && this.renderBaseFilter()}
                                            </Grid>
                                            <Grid item>
                                                <IconCheckComplete /><span>&nbsp;{progress.toFixed(0)}%</span>
                                            </Grid>
                                            <Grid item>
                                                <TextPluralization
                                                    count={completed}
                                                    resource="Common.Feed.Course.Filter.CompletedResult"
                                                />
                                            </Grid>
                                        </>
                                    )
                            }
                        </Grid>
                    </Box>
                </Toolbar>
            </MuiFilterAppBar>
        );
    }


    //#endregion

    ///
    public feedReset() {
        this.props.onFeedReset();
    }

    ///
    public componentDidMount() {
        this.feedReset();
    }

    ///
    public componentWillUnmount() {
        this.feedReset();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.courseId != this.props.courseId) {
            this.feedReset();
        }
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, totalRecords, loading }, snackbar } = this.props;

        return (
            <>
                {this.state.dialog}

                <MuiContainer margin="normal">
                    {this.renderFilterBar()}
                </MuiContainer>

                <CardManager
                    batchSize={999}
                    classes={classes}
                    feedType={FeedType.CourseViewer}
                    feed={feed}
                    snackbar={snackbar}
                    onRemoveFromFeed={this.props.onRemoveFromFeed}
                    onReloadPublication={this.props.onReloadPublication}
                    onExpandPublication={this.props.onExpandPublication}
                    onUpdatePublication={this.props.onUpdateFeedData}
                    onCustomAction={(uid: string, publicationId: string, publicationType: PublicationType, action: any) => this.handleCustomAction(uid, publicationId, publicationType, action)}
                    onGetFeed={(startIndex: number, endIndex: number) => this.props.onGetFeed(this.filter, startIndex, endIndex)}
                />

                {
                    lastError != null
                        ? <RequestErrorReport error={lastError} />
                        : totalRecords == 0 && !loading && !this.isFiltered()
                            ? <MuiMessageBox variant="info" message={this.props.t("Common.Feed.Course.Filter.ListItemEmpty")} />
                            : null
                }
            </>
        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feedPrivate
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispatchProps {
    const action = new FeedActions.CourseEnrollFeedAction(ownProps.courseId);

    return {
        onCustomAction: (uid, publicationId, promiseAction) => dispatch(action.customAction(uid, publicationId, promiseAction)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onFeedReset: () => dispatch(action.feedReset()),
        onGetFeed: (filter, startIndex, endIndex) => dispatch(action.getFeed(filter, startIndex, endIndex)),
    } as IDispatchProps;
}

export default connect<IStateProps, IDispatchProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(FeedCourseEnrollComponent)));